import { useState, useEffect, useCallback, useMemo } from 'react';
import { RoomEvent, RoomStateEvent, ClientEvent, MatrixEventEvent, UserEvent } from 'matrix-js-sdk';
import { useMatrixClient } from '../context/MatrixContext';
import { useAvatar } from '../context/AvatarContext';
import useRoomReadReceipts from './useRoomReadReceipts';
import useTimelineWindow from './useTimelineWindow';
import { useUnreadTracking } from './useUnreadTracking';
import { groupMessages } from '../utils/conversation';
import { SUPPORTED_MESSAGE_TYPES } from '../constants';
import { TIMELINE_SYSTEM_EVENT_TYPES } from '../constants';
import { useEventShields } from './useEventShields';
import { generateInitialsAvatar } from '../utils/avatar';
import { generateThumbnail, getMediaDimensions } from '../utils/thumbnailGenerator';
import logger from '../utils/logger';
import mediaCacheService from '../services/mediaCacheService';
import { getRoomDisplayName, getRoomTopic } from '../utils/gematikRoomUtils';

/**
 * A hook to manage a Matrix room's state, including its messages and timeline.
 * It provides a reactive list of messages and a function to send new messages.
 * Now includes timeline pagination and memory management via TimelineWindow.
 *
 * @param {string} roomId The ID of the room to connect to.
 * @returns {{
 *   roomId: string,
 *   name: string,
 *   members: import('matrix-js-sdk').RoomMember[],
 *   messageGroups: object[],
 *   sendMessage: (content: object) => Promise<{event_id: string}>,
 *   toggleReaction: (eventId: string, key: string, reactionEventId?: string, allReactions?: Map) => Promise<{event_id: string}>,
 *   deleteMessage: (eventId: string, reason?: string) => Promise<{event_id: string}>,
 *   isRoomLoading: boolean,
 *   roomExists: boolean,
 *   isLoadingOlder: boolean,
 *   isLoadingNewer: boolean,
 *   canPaginateBackward: boolean,
 *   canPaginateForward: boolean,
 *   windowPosition: string,
 *   isFillingViewport: boolean,
 *   paginateBackward: () => Promise<void>,
 *   paginateForward: () => Promise<void>,
 *   jumpToLive: () => void,
 *   unreadCount: number,
 *   firstUnreadEventId: string | null,
 *   hasUnread: boolean,
 * }}
 * An object containing the client instance, the room's derived state, functions
 * to interact with it, loading/existence flags, and timeline pagination controls.
 */
export const useRoom = (roomId) => {
  const client = useMatrixClient();
  const [room, setRoom] = useState(null);
  const [name, setName] = useState('');
  const [topic, setTopic] = useState('');
  const [members, setMembers] = useState([]);
  const [isRoomLoading, setIsRoomLoading] = useState(true);
  const [decryptionCounter, setDecryptionCounter] = useState(0);
  const [memberAvatars, setMemberAvatars] = useState(new Map());
  const [readMarkerId, setReadMarkerId] = useState(null);
  const [roomAvatars, setRoomAvatars] = useState([]);
  const [encryptionStateCounter, setEncryptionStateCounter] = useState(0);
  
  // Use the new timeline window hook for better pagination and memory management
  const {
    events,
    isLoadingOlder,
    isLoadingNewer,
    canPaginateBackward,
    canPaginateForward,
    windowPosition,
    isFillingViewport,
    paginateBackward,
    paginateForward,
    jumpToLive,
  } = useTimelineWindow(room, roomId);
  
  const readReceipts = useRoomReadReceipts(room);
  const eventShields = useEventShields(room);

  // Check if the room is encrypted by looking for m.room.encryption state event
  // Include encryptionStateCounter to recompute when state changes
  const isEncrypted = useMemo(() => {
    if (!room) return false;
    const encryptionEvent = room.currentState.getStateEvents('m.room.encryption', '');
    return !!encryptionEvent;
  }, [room, encryptionStateCounter]);

  // Effect 1: Find the room object and update the `room` state.
  useEffect(() => {
    logger.debug(`useRoom (finder): Effect triggered for roomId: ${roomId}`);
    if (!client || !roomId) {
      setRoom(null);
      setIsRoomLoading(false);
      return;
    }

    // Reset state when roomId changes
    setRoom(null);
    setName('');
    setMembers([]);
    setIsRoomLoading(true);

    const roomObject = client.getRoom(roomId);
    if (roomObject) {
      logger.debug(`useRoom (finder): Room ${roomId} found immediately.`);
      setRoom(roomObject);
      return;
    }

    logger.debug(`useRoom (finder): Room ${roomId} not found. Listening for ClientEvent.Room.`);
    const onClientRoom = (updatedRoom) => {
      if (updatedRoom.roomId === roomId) {
        logger.debug(`useRoom (finder): Matched room ${roomId} from ClientEvent.Room.`);
        setRoom(updatedRoom);
        client.removeListener(ClientEvent.Room, onClientRoom);
      }
    };
    client.on(ClientEvent.Room, onClientRoom);

    return () => {
      logger.debug(`useRoom (finder): Cleaning up ClientEvent.Room listener for ${roomId}.`);
      client.removeListener(ClientEvent.Room, onClientRoom);
    };
  }, [client, roomId]);

  // Effect 2: Set up listeners and initial state once we have a room object.
  useEffect(() => {
    if (!room || room.roomId !== roomId) {
      return; // Do nothing if we don't have a room object yet.
    }

    logger.debug(`useRoom (listener): Setting up for room ${room.roomId}`);

    // Compute and set the display name using the utility (prioritizes Gematik)
    const displayName = getRoomDisplayName(room);
    setName(displayName);

    const roomTopic = getRoomTopic(room);
    setTopic(roomTopic);

    const allMembers = room.currentState.getMembers().filter(m => 
      m.membership === 'join' || m.membership === 'invite',
    );

    setMembers([...allMembers]);
    setIsRoomLoading(false);

    // --- Set Initial Read Marker ---
    // This logic runs once when the room is loaded to find the initial "new messages" line.
    const myUserId = client.getUserId();
    if (myUserId) {
      // First, check for the 'm.fully_read' account data event. This is the most reliable marker.
      const fullyReadEvent = room.getAccountData('m.fully_read');
      const markerEventId = fullyReadEvent?.getContent()?.event_id;

      logger.debug('[ReadMarker] markerEventId:', markerEventId);
      if (markerEventId) {
        setReadMarkerId(markerEventId);
      } else {
        // If no 'm.fully_read' marker, fall back to the user's latest read receipt.
        const readUpToId = room.getEventReadUpTo(myUserId);
        setReadMarkerId(readUpToId);
      }
    }

    // Define listeners for room state (not timeline - that's handled by useTimelineWindow)
    const onName = (roomInEvent) => {
      if (roomInEvent.roomId === room.roomId) setName(roomInEvent.name);
    };

    const onTopic = (roomInEvent) => {
      if (roomInEvent.roomId === room.roomId) setTopic(roomInEvent.topic);
    };

    const onStateUpdate = (state) => {
      if (state.roomId === room.roomId) {
        const allMembers = room.currentState.getMembers().filter(m =>
          m.membership === 'join' || m.membership === 'invite',
        );
        setMembers([...allMembers]);
        // Update name considering Gematik custom name
        const displayName = getRoomDisplayName(room);
        setName(displayName);

        const roomTopic = getRoomTopic(room);
        setTopic(roomTopic);
        // Trigger encryption state recalculation when state updates
        setEncryptionStateCounter(c => c + 1);
      }
    };

    // Attach listeners (timeline listeners are handled by useTimelineWindow)
    room.on(RoomEvent.Name, onName);
    room.on(RoomEvent.Topic, onTopic);
    room.currentState.on(RoomStateEvent.Update, onStateUpdate);

    // Return a cleanup function
    return () => {
      logger.debug(`useRoom (listener): Cleaning up listeners for room ${room.roomId}`);
      room.removeListener(RoomEvent.Name, onName);
      room.removeListener(RoomEvent.Topic, onTopic);
      room.currentState.removeListener(RoomStateEvent.Update, onStateUpdate);
    };
  }, [room, client, roomId]);

  // Effect 3: Handle decryption events separately to avoid re-binding main listeners.
  useEffect(() => {
    if (!room || !client) {
      return;
    }

    const onEventDecrypted = (event) => {
      // If a decryption happens for an event in our room, we want to trigger
      // a re-calculation of messageGroups. A simple counter increment is the
      // cleanest way to do this without creating complex dependencies.
      if (event.getRoomId() === room.roomId) {
        logger.debug(`useRoom: Decryption event in ${room.roomId}, forcing update.`);
        setDecryptionCounter(c => c + 1);
      }
    };

    client.on(MatrixEventEvent.Decrypted, onEventDecrypted);
    return () => {
      client.removeListener(MatrixEventEvent.Decrypted, onEventDecrypted);
    };
  }, [client, room]); // This effect no longer depends on the messages array.

  // Effect 4: Keep the crypto module's tracked user list in sync with room members.
  useEffect(() => {
    if (client && members.length > 0) {
      const userIds = members.map(m => m.userId);
      logger.debug(`useRoom: Tracking ${userIds.length} users for device list updates.`);
      // This tells the crypto module to be aware of these users' devices.
      // It's essential for calculating e2ee shield states correctly.
      // We don't need the result, but calling this triggers the download of device
      // keys for any users that are not already known.
      const crypto = client.getCrypto();
      if (crypto) {
        crypto.getUserDeviceInfo(userIds);
      }
    }
  }, [client, members]);

  // Create stable member identifier for avatar effect dependency
  // Only changes when members are added/removed, not on every state update
  const memberIds = useMemo(() => {
    return members.map(m => m.userId).sort().join(',');
  }, [members]);

  // Effect 5: Load member avatars asynchronously with authenticated fetch
  const { getUserAvatar } = useAvatar();
  
  useEffect(() => {
    if (!client || members.length === 0) {
      return;
    }

    let isMounted = true;

    const loadAvatars = async () => {
      const newAvatarMap = new Map();

      // Load all avatars in parallel
      await Promise.all(
        members.map(async (m) => {
          const displayName = m.rawDisplayName || m.name || m.userId;
          try {
            // Get avatar via context (handles lifecycle automatically)
            const avatarUrl = await getUserAvatar(m.userId, displayName, 96);
            
            if (isMounted) {
              newAvatarMap.set(m.userId, avatarUrl);
            }
          } catch (error) {
            logger.error(`Failed to load avatar for ${m.userId}:`, error);
            // Fallback to initials avatar
            if (isMounted) {
              newAvatarMap.set(m.userId, generateInitialsAvatar(displayName));
            }
          }
        }),
      );

      if (isMounted) {
        setMemberAvatars(newAvatarMap);
      }
    };

    loadAvatars();

    return () => {
      isMounted = false;
      // No cleanup needed - AvatarContext handles it automatically
    };
  }, [client, memberIds, getUserAvatar]);
  // Note: 'members' is intentionally omitted. We only want to re-run when member IDs change
  // (additions/removals), not when member objects are updated. The memberIds memo tracks this.

  // Listen for custom avatar change events (immediate updates on own upload)
  // This complements UserEvent.AvatarUrl which only fires for other users
  const { invalidateAvatar } = useAvatar();
  
  useEffect(() => {
    if (!client || members.length === 0) {
      return;
    }

    const handleCustomAvatarChange = async (event) => {
      const { userId: changedUserId } = event.detail || {};
      if (!changedUserId) {
        return;
      }

      // Check if this user is a member of this room
      const isMember = members.some(m => m.userId === changedUserId);
      if (!isMember) {
        return;
      }

      logger.debug(`Custom avatar event: ${changedUserId} in room ${roomId}`);

      // Invalidate cache via context
      await invalidateAvatar(changedUserId);

      // Re-fetch avatar for this specific user
      const member = members.find(m => m.userId === changedUserId);
      if (!member) return;

      const displayName = member.rawDisplayName || member.name || member.userId;

      try {
        const newAvatarUrl = await getUserAvatar(changedUserId, displayName, 96);

        // Update memberAvatars map
        setMemberAvatars(prev => {
          const updated = new Map(prev);
          updated.set(changedUserId, newAvatarUrl);
          return updated;
        });

        logger.debug(`Avatar updated: ${changedUserId} in room ${roomId}`);
      } catch (error) {
        logger.error(`Failed to update avatar for ${changedUserId}:`, error);
      }
    };

    window.addEventListener('matrix:avatar:changed', handleCustomAvatarChange);

    return () => {
      window.removeEventListener('matrix:avatar:changed', handleCustomAvatarChange);
    };
  }, [client, members, roomId, invalidateAvatar, getUserAvatar]);

  // Listen for Matrix SDK avatar change events (sync-based updates from other users)
  // IMPORTANT: We listen to BOTH UserEvent.AvatarUrl AND RoomStateEvent.Members for defense in depth.
  // UserEvent.AvatarUrl often doesn't fire when users change avatars, but RoomStateEvent.Members
  // always fires because avatar changes come as m.room.member state events. Keeping both listeners
  // ensures we catch avatar updates through either path.
  useEffect(() => {
    if (!client || members.length === 0) {
      return;
    }

    const handleUserAvatarChange = async (event, user) => {
      const changedUserId = user.userId;

      // Check if this user is a member of this room
      const isMember = members.some(m => m.userId === changedUserId);
      if (!isMember) {
        return;
      }

      logger.debug(`UserEvent.AvatarUrl: ${changedUserId} in room ${roomId}`);

      // Invalidate and re-fetch via context
      await invalidateAvatar(changedUserId);

      const member = members.find(m => m.userId === changedUserId);
      if (!member) return;

      const displayName = member.rawDisplayName || member.name || member.userId;

      try {
        const newAvatarUrl = await getUserAvatar(changedUserId, displayName, 96);

        setMemberAvatars(prev => {
          const updated = new Map(prev);
          updated.set(changedUserId, newAvatarUrl);
          return updated;
        });

        logger.debug(`Avatar updated: ${changedUserId} in room ${roomId}`);
      } catch (error) {
        logger.error(`Failed to update avatar for ${changedUserId}:`, error);
      }
    };

    // Listen for RoomStateEvent.Members to catch avatar changes in m.room.member events
    // This is the PRIMARY mechanism - UserEvent.AvatarUrl often doesn't fire for avatar changes
    const handleMemberStateChange = async (event, state, member) => {
      // Only process m.room.member events
      if (event.getType() !== 'm.room.member') return;

      // Only process events for this room
      if (event.getRoomId() !== roomId) return;

      const content = event.getContent();
      const prevContent = event.getPrevContent();

      // Check if avatar_url changed
      if (content.avatar_url !== prevContent?.avatar_url) {
        const changedUserId = event.getStateKey();

        // Check if this user is a member of this room
        const isMember = members.some(m => m.userId === changedUserId);
        if (!isMember) {
          return;
        }

        logger.debug(`RoomStateEvent.Members avatar change: ${changedUserId} in room ${roomId}`);

        // Invalidate and re-fetch via context
        await invalidateAvatar(changedUserId);

        const memberObj = members.find(m => m.userId === changedUserId);
        if (!memberObj) return;

        const displayName = memberObj.rawDisplayName || memberObj.name || memberObj.userId;

        try {
          const newAvatarUrl = await getUserAvatar(changedUserId, displayName, 96);

          setMemberAvatars(prev => {
            const updated = new Map(prev);
            updated.set(changedUserId, newAvatarUrl);
            return updated;
          });

          logger.debug(`Avatar updated via RoomStateEvent: ${changedUserId} in room ${roomId}`);
        } catch (error) {
          logger.error(`Failed to update avatar for ${changedUserId}:`, error);
        }
      }
    };

    client.on(UserEvent.AvatarUrl, handleUserAvatarChange);
    client.on(RoomStateEvent.Members, handleMemberStateChange);

    return () => {
      client.off(UserEvent.AvatarUrl, handleUserAvatarChange);
      client.off(RoomStateEvent.Members, handleMemberStateChange);
    };
  }, [client, members, roomId, invalidateAvatar, getUserAvatar]);

  const sendMessage = useCallback(async (content) => {
    if (!client || !roomId) {
      throw new Error('Matrix client or room ID not available.');
    }

    return client.sendMessage(roomId, content);
  }, [client, roomId]);

  const deleteMessage = useCallback(async (eventId, reason) => {
    if (!client || !roomId) {
      throw new Error('Matrix client or room ID not available for delete.');
    }

    // Find the event to check if it's an attachment that needs cache clearing.
    const event = room?.findEventById(eventId);
    if (event) {
      const content = event.getContent();
      const isAttachment = ['m.image', 'm.video', 'm.audio', 'm.file'].includes(content?.msgtype);

      // If it's an attachment with a URL, remove it from the IndexedDB cache.
      if (isAttachment && content.url) {
        try {
          logger.debug(`[useRoom] Deleting attachment from cache: ${content.url}`);
          await mediaCacheService.removeMedia(content.url);
        } catch (error) {
          // Log the error but don't block the message deletion.
          logger.error(`[useRoom] Failed to remove attachment from cache: ${content.url}`, error);
        }
      }
    }

    // The `reason` can be passed in the content of the redaction event.
    const redactionContent = (reason) ? { reason } : {};
    return client.redactEvent(roomId, eventId, undefined, redactionContent);
  }, [client, roomId, room]);

  const leaveRoom = useCallback(async () => {
    if (!client || !roomId) {
      throw new Error('Matrix client or room ID not available.');
    }

    // Clear all cached media for this room from IndexedDB before leaving.
    try {
      logger.debug(`[useRoom] Clearing media cache for room on leave: ${roomId}`);
      await mediaCacheService.removeMediaForRoom(roomId);
    } catch (error) {
      logger.error(`[useRoom] Failed to clear media cache for room ${roomId}:`, error);
    }

    return client.leave(roomId);
  }, [client, roomId]);

  const toggleReaction = useCallback(async (eventId, key, reactionEventId = null, allReactions = new Map()) => {
    if (!client || !roomId) {
      throw new Error('Matrix client or room ID not available.');
    }

    const currentUserId = client.getUserId();

    // If reactionEventId is provided, this is an unreact action
    if (reactionEventId) {
      return client.redactEvent(roomId, reactionEventId);
    }

    // Check if user has already reacted with a different emoji
    let existingReactionEventId = null;
    allReactions.forEach((reactors, emoji) => {
      if (emoji !== key) { // Different emoji
        const userReaction = reactors.find(reactor => reactor.senderId === currentUserId);
        if (userReaction) {
          existingReactionEventId = userReaction.eventId;
        }
      }
    });

    // If user has an existing reaction with a different emoji, remove it first
    if (existingReactionEventId) {
      await client.redactEvent(roomId, existingReactionEventId);
    }

    // Send the new reaction
    const content = {
      'm.relates_to': {
        'rel_type': 'm.annotation',
        'event_id': eventId,
        'key': key,
      },
    };

    return client.sendEvent(roomId, 'm.reaction', content);
  }, [client, roomId]);

  const canDeleteMessage = useCallback(async (eventId) => {
    if (!client || !roomId || !room) {
      logger.warn('useRoom: Cannot check delete permissions - client, room, or roomId not available');
      return false;
    }

    try {
      // Get the event (message) to check
      const event = room.findEventById(eventId);
      if (!event) {
        logger.warn('useRoom: Event not found for delete permission check:', eventId);
        return false;
      }

      const currentUserId = client.getUserId();

      // Check if user is the sender of the message
      const isOwnMessage = event.getSender() === currentUserId;

      // Check if user has permission to redact events in this room
      let canRedact = false;

      if (room.currentState) {
        const powerLevels = room.currentState.getStateEvents('m.room.power_levels', '');

        if (powerLevels) {
          const powerLevelContent = powerLevels.getContent();
          const userPowerLevel = powerLevelContent.users?.[currentUserId] || powerLevelContent.users_default || 0;
          const redactPowerLevel = powerLevelContent.redact || 50;

          // Per Matrix spec: Users can redact others' messages if power level >= redact level
          // Default redact level is 50 (moderator). Users can always delete their own messages.
          canRedact = userPowerLevel >= redactPowerLevel;
        }
      }

      // User can delete if it's their own message OR they have redact permissions
      const canDelete = isOwnMessage || canRedact;

      logger.debug('useRoom: Delete permission check:', {
        roomId,
        eventId,
        currentUserId,
        isOwnMessage,
        canRedact,
        canDelete,
      });

      return canDelete;
    } catch (error) {
      logger.error('useRoom: Error checking delete permissions:', error);
      return false;
    }
  }, [client, roomId, room]);

  const setReadMarker = useCallback(async (eventId, destinationRoomId, readReceiptEventId) => {
    if (!client || !destinationRoomId || !eventId) return;
    try {
      // Update the server with the new read marker position
      await client.setRoomReadMarkers(destinationRoomId, eventId, readReceiptEventId);
      // only update local state if it's not due to room change cleanup
      if (destinationRoomId === roomId) {
        setReadMarkerId(eventId);
      }
      logger.debug(`[ReadMarker] Read marker set to ${eventId} in room ${destinationRoomId}, current is ${roomId}`);
    } catch {
      // Don't show a UI error for this, as it's a background task, but log it.
      logger.error(`[ReadMarker] Failed to set read marker for room ${destinationRoomId}:`);
    }
  }, [client, roomId]);


  const sendAttachment = useCallback(async (file, caption = '', options = {}) => {
    if (!client || !roomId) {
      throw new Error('Matrix client or room ID not available.');
    }

    if (!file) {
      throw new Error('File is required for attachment.');
    }

    const { abortController, thumbnailResult, onProgressUpdate, replyEventId } = options;

    try {
      // Check for abort before starting
      if (abortController?.signal?.aborted) {
        throw new Error('Upload cancelled');
      }

      // Upload the file to the Matrix content repository
      // Main file upload is 90% of total progress
      const uploadResponse = await client.uploadContent(file, {
        name: file.name,
        type: file.type,
        abortController,
        progressHandler: (progress) => {
          if (onProgressUpdate && progress.total > 0) {
            // Main file is 90% of total progress
            const percentage = Math.round((progress.loaded / progress.total) * 90);
            onProgressUpdate(Math.max(0, Math.min(90, percentage)));
          }
        },
      });

      // Determine message type based on file type
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      const isAudio = file.type.startsWith('audio/');
      
      let msgtype = 'm.file'; // Default
      if (isImage) msgtype = 'm.image';
      else if (isVideo) msgtype = 'm.video';
      else if (isAudio) msgtype = 'm.audio';

      // Create the basic message content
      const content = {
        body: caption.trim() || file.name,
        filename: file.name,
        info: {
          size: file.size,
          mimetype: file.type,
        },
        msgtype,
        url: uploadResponse.content_uri,
        ['m.relates_to']: replyEventId ? {
          'm.in_reply_to': {
            'event_id': replyEventId,
          },
        } : undefined,
      };

      // For images and videos, add dimensions and generate thumbnail
      if (isImage || isVideo) {
        // Get media dimensions using utility function
        const mediaDimensions = await getMediaDimensions(file);

        // Add media dimensions to info
        if (mediaDimensions?.w && mediaDimensions?.h) {
          content.info.w = mediaDimensions.w;
          content.info.h = mediaDimensions.h;
          
          // Add duration for videos
          if (isVideo && mediaDimensions.duration) {
            content.info.duration = mediaDimensions.duration;
          }
        }

        // Generate and upload thumbnail
        try {
          // Check for abort before thumbnail upload
          if (abortController?.signal?.aborted) {
            throw new Error('Upload cancelled');
          }

          // Use pre-generated thumbnail if provided, otherwise generate new one
          let thumbnailToUpload = thumbnailResult;
          if (!thumbnailToUpload) {
            thumbnailToUpload = await generateThumbnail(file);
          }

          if (thumbnailToUpload) {
            // Check for abort before thumbnail upload
            if (abortController?.signal?.aborted) {
              throw new Error('Upload cancelled');
            }

            const thumbnailResponse = await client.uploadContent(thumbnailToUpload.thumbnail, {
              name: `thumb_${file.name}`,
              type: 'image/jpeg',
              abortController,
              progressHandler: (progress) => {
                if (onProgressUpdate && progress.total > 0) {
                  // Thumbnail is final 10% of progress (90-100%)
                  const percentage = 90 + Math.round((progress.loaded / progress.total) * 10);
                  onProgressUpdate(Math.max(90, Math.min(100, percentage)));
                }
              },
            });

            // Add thumbnail info using Element Web format
            content.info.thumbnail_url = thumbnailResponse.content_uri;
            content.info.thumbnail_info = thumbnailToUpload.info.thumbnail_info;
          }
        } catch (thumbnailError) {
          logger.warn('Failed to generate thumbnail:', thumbnailError);
          // Continue without thumbnail - this is not a critical error
        }
      }

      // Check for abort before sending the message
      if (abortController?.signal?.aborted) {
        throw new Error('Upload cancelled');
      }

      // Send the message with the uploaded file
      return client.sendEvent(roomId, 'm.room.message', content);
    } catch (error) {
      // Reset progress on error
      if (onProgressUpdate) {
        onProgressUpdate(-1);
      }

      // Check if this is an AbortError and convert to standard format
      if (error.message?.includes('cancelled') || error.name === 'AbortError') {
        const abortError = new Error('Upload cancelled');
        abortError.name = 'AbortError';
        throw abortError;
      }

      logger.error('Failed to send attachment:', error);
      throw error;
    }
  }, [client, roomId]);

  const enrichedMembers = useMemo(() => {
    if (!members.length || !client || !room) return new Map();

    const memberMap = new Map();

    // Get power levels from room state with validation
    const powerLevelsEvent = room.currentState.getStateEvents('m.room.power_levels', '');
    const powerLevels = powerLevelsEvent?.getContent() || {};

    // Validate users_default is a number, fallback to 0
    const usersDefault = typeof powerLevels.users_default === 'number'
      && !isNaN(powerLevels.users_default)
      ? powerLevels.users_default
      : 0;

    // Validate users object exists and is an object
    const usersMap = powerLevels.users
      && typeof powerLevels.users === 'object'
      && !Array.isArray(powerLevels.users)
      ? powerLevels.users
      : {};

    members.forEach(m => {
      const displayName = m.rawDisplayName || m.name || m.userId;

      // Get avatar from loaded avatar state (async loaded with authentication)
      // Falls back to initials if not yet loaded
      const avatar = memberAvatars.get(m.userId) || generateInitialsAvatar(displayName);

      // Get user's power level with validation
      const rawPowerLevel = usersMap[m.userId];
      const userPowerLevel = typeof rawPowerLevel === 'number'
        && !isNaN(rawPowerLevel)
        ? rawPowerLevel
        : usersDefault;

      memberMap.set(m.userId, {
        ...m, // Keep original member properties
        id: m.userId, // Add id property for ContactList compatibility
        name: displayName,
        avatar: avatar,
        powerLevel: userPowerLevel,
      });
    });

    return memberMap;
  }, [members, client, memberAvatars, room]);

  // Effect: Fetch room avatars - explicit room avatar or fallback to member avatars
  const { getRoomAvatar, getFallbackAvatars } = useAvatar();
  
  useEffect(() => {
    if (!room || !client) {
      setRoomAvatars([]);
      return;
    }

    let isMounted = true;

    const fetchRoomAvatars = async () => {
      try {
        const roomName = room.name || 'Chat';
        
        // First, try to get explicit room avatar
        const explicitAvatar = await getRoomAvatar(room.roomId, roomName, 40);
        
        if (explicitAvatar) {
          // Room has an explicit avatar set
          if (isMounted) {
            setRoomAvatars([explicitAvatar]);
          }
        } else {
          // No explicit avatar - get fallback member avatars
          const fallbackAvatars = await getFallbackAvatars(room.roomId, 96);
          if (isMounted) {
            const avatarUrls = fallbackAvatars.map(desc => desc.url).filter(Boolean);
            setRoomAvatars(avatarUrls);
          }
        }
      } catch (error) {
        logger.error('Failed to fetch room avatars:', error);
        if (isMounted) {
          setRoomAvatars([]);
        }
      }
    };

    fetchRoomAvatars();

    return () => {
      isMounted = false;
    };
  }, [room, client, enrichedMembers, getRoomAvatar, getFallbackAvatars]);

  const processMessageEdits = (events) => {
    // Find edit events and track which original events they reference
    const editedEventIds = new Set(); // IDs of original events that have been edited
    const editEventIds = new Set(); // IDs of edit events to filter out
    const editContentMap = new Map(); // Map of original event ID -> new content

    events.forEach(event => {
      // Check both regular content and clear content (for encrypted messages)
      const content = event.getContent();
      const clearContent = event.getClearContent && event.getClearContent();
      const contentToCheck = clearContent || content;
      
      // Check if this is an edit event
      if (contentToCheck && contentToCheck['m.new_content'] && contentToCheck['m.relates_to']) {
        const relatesTo = contentToCheck['m.relates_to'];
        
        // Check if it's a replace relation (edit)
        if (relatesTo.rel_type === 'm.replace' && relatesTo.event_id) {
          const originalEventId = relatesTo.event_id;
          
          // Track that the original event has been edited
          editedEventIds.add(originalEventId);
          
          // Store the new content for this original event
          editContentMap.set(originalEventId, contentToCheck['m.new_content']);
          
          // Mark this edit event for removal
          editEventIds.add(event.getId());
        }
      }
    });

    // Filter out edit events and mark original events as edited
    const processedEvents = events.filter(event => {
      return !editEventIds.has(event.getId());
    }).map(event => {
      const eventId = event.getId();
      
      // Mark event as edited if it has been edited and store the new content
      if (editedEventIds.has(eventId)) {
        event._isEdited = true;
        event._editedContent = editContentMap.get(eventId);
      }
      
      return event;
    });

    return processedEvents;
  };

  const processReactions = (events) => {
    // Create a map of eventId -> reactions
    const reactionsByMessage = new Map();

    events.forEach(event => {
      if (event.getType() === 'm.reaction') {
        const content = event.getContent();
        const relatesTo = content['m.relates_to'];
        
        if (relatesTo && relatesTo.rel_type === 'm.annotation' && relatesTo.event_id) {
          const targetEventId = relatesTo.event_id;
          const reactionKey = relatesTo.key;
          const senderId = event.getSender();
          
          if (!reactionsByMessage.has(targetEventId)) {
            reactionsByMessage.set(targetEventId, new Map());
          }
          
          const messageReactions = reactionsByMessage.get(targetEventId);
          if (!messageReactions.has(reactionKey)) {
            messageReactions.set(reactionKey, []);
          }
          
          messageReactions.get(reactionKey).push({
            senderId,
            eventId: event.getId(),
          });
        }
      }
    });

    return reactionsByMessage;
  };

  const messageGroups = useMemo(() => {
    if (!client || !events) return [];

    // Process reactions first to create a map of reactions by message
    const reactionsByMessage = processReactions(events);

    // Filter for supported message types, including encrypted ones and room creation
    const messageEvents = events.filter(event => {
      const type = event.getType();
      
      // Always include redacted messages so we can show "Message deleted"
      if (event.isRedacted()) {
        return true;
      }
      
      // Include room creation events
      if (type === 'm.room.create') {
        return true;
      }
      // Check if it's a decrypted message (was encrypted but now has clearContent)
      if (event.getClearContent && event.getClearContent()) {
        const clearContent = event.getClearContent();
        return SUPPORTED_MESSAGE_TYPES.includes(clearContent.msgtype);
      }
      // Check regular unencrypted messages
      if (type === 'm.room.message') {
        const content = event.getContent();
        return SUPPORTED_MESSAGE_TYPES.includes(content.msgtype);
      }
      // Include encrypted messages that failed to decrypt
      // so users can at least see that messages exist
      if (type === 'm.room.encrypted') {
        return true;
      }
      // Otherwise include events that typically generate timeline messages
      return TIMELINE_SYSTEM_EVENT_TYPES.includes(type);
    });


    const processedEvents = processMessageEdits(messageEvents);

    const currentUserId = client.getUserId();
    const grouped = groupMessages(processedEvents);

    return grouped.map((messageGroup, groupIndex) => {
      const firstMessage = messageGroup[0];
      const senderId = firstMessage.getSender();
      const messageId = firstMessage.getId();
      const member = enrichedMembers.get(senderId);
      const shield = eventShields.get(messageId);

      // Get display name with fallback to event sender (for users who left)
      // Matrix events preserve sender RoomMember snapshot at time of sending
      const displayName = member?.name || firstMessage.sender?.name || senderId;

      // Add reactions to each message in the group
      const messagesWithReactions = messageGroup.map(message => {
        const messageId = message.getId();
        const messageReactions = reactionsByMessage.get(messageId) || new Map();
        
        // Enrich reactions with user display names
        const enrichedReactions = new Map();
        messageReactions.forEach((reactors, emoji) => {
          const enrichedReactors = reactors.map(reactor => {
            const member = enrichedMembers.get(reactor.senderId);
            return {
              ...reactor,
              displayName: member?.name || reactor.senderId,
              isCurrentUser: reactor.senderId === currentUserId,
            };
          });
          enrichedReactions.set(emoji, enrichedReactors);
        });
        
        // Attach enriched reactions to message for easy access in components
        message._reactions = enrichedReactions;
        
        return message;
      });

      // Use member avatar if available (already loaded with authentication)
      // Falls back to initials avatar if not available
      let avatar = member?.avatar;
      if (!avatar) {
        avatar = memberAvatars.get(senderId) || generateInitialsAvatar(displayName);
      }

      return {
        key: `group-${groupIndex}-${messageId}`,
        senderId,
        messages: messagesWithReactions,
        name: displayName,
        avatar: avatar,
        shield: shield,
        isUser: senderId === currentUserId,
      };
    });
  }, [client, events, decryptionCounter, readReceipts, enrichedMembers, eventShields, memberAvatars]);

  // Integrate unread tracking
  const { unreadCount, firstUnreadEventId, hasUnread } = useUnreadTracking({
    room,
    client,
    messageGroups,
  });

  return {
    room,
    client,
    roomId,
    name,
    topic,
    members: Array.from(enrichedMembers.values()),
    roomAvatars,
    memberAvatars,
    messageGroups,
    readReceipts,
    toggleReaction,
    sendMessage,
    sendAttachment,
    deleteMessage,
    canDeleteMessage,
    leaveRoom,
    isRoomLoading,
    roomExists: !!room,
    isEncrypted,
    // Timeline pagination controls
    isLoadingOlder,
    isLoadingNewer,
    canPaginateBackward,
    canPaginateForward,
    windowPosition,
    isFillingViewport,
    paginateBackward,
    paginateForward,
    jumpToLive,
    readMarkerId,
    setReadMarker,
    // Unread tracking
    unreadCount,
    firstUnreadEventId,
    hasUnread,
  };
};

export default useRoom;
